import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../material.module';
import { ProjectDetailsService } from '../../services/project-details.service';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';

export interface DuplicateSpacerDialogData {
    slurryId: number;
    projectId: number;
    type: 'Slurry' | 'Spacer';
}

@Component({
    selector: 'app-duplicate-spacer-dialog',
    standalone: true,
    imports: [MaterialModule, FormsModule, SpinnerComponent],
    templateUrl: './duplicate-spacer-dialog.component.html',
    styleUrls: ['./duplicate-spacer-dialog.component.css'],
})
export class DuplicateSpacerDialogComponent {
    redesign = false;
    redesignReason: 'Compliance' | 'Logistics' | 'Cost' | null = null;
    comments = '';
    selectedTests: Record<string, boolean> = {};
    isSaving = false;
    saveError: string | null = null;

    readonly slurryComplianceTestTypeMap: Record<string, number> = {
        'Thickening Time': 1,
        'Fluid Loss': 2,
        'Free Fluid': 3,
        'Rheology': 4,
        'HPHT Rheology': 5,
        'Static Gel Strength': 6,
        'Foam Stability (Cement)': 7,
        'Foam Stability (Slurry)': 8,
        'Pressurized Mud Scale': 10,
        'Api Sedimentation': 12,
        'Expansion': 13,
        'Shear Bond': 14,
        'Acid Solubility': 15,
        'Basic Crush': 16,
        'Splitting TS': 17,
        'Unconfined CS': 18,
        'Triaxial Confined CS': 19,
        'Compressive Strength (UCA)': 20,
        'Compressive Strength (Crush)': 21,
        'Mixability': 33,
        'Compressive Strength (Conditioning)': 34,
    };

    readonly slurryComplianceTestColumns: string[][] = [
        ['Thickening Time', 'Rheology', 'Foam Stability (Cement)', 'Pyconometer', 'Shear Bond', 'Splitting TS', 'Compressive Strength (UCA)', 'Mixability'],
        ['Fluid Loss', 'HPHT Rheology', 'Foam Stability (Slurry)', 'Api Sedimentation', 'Acid Solubility', 'Unconfined CS', 'Compressive Strength (Crush)'],
        ['Free Fluid', 'Static Gel Strength', 'Pressurized Mud Scale', 'Expansion', 'Basic Crush', 'Triaxial Confined CS', 'Compressive Strength (Conditioning)'],
    ];

    // Spacer-specific compliance tests
    readonly spacerComplianceTests = [
        { label: 'Spacer Compatibility', id: 101 },
        { label: 'Spacer SSST', id: 102 },
        { label: 'Spacer QAQC', id: 103 },
        { label: 'Mixability', id: 33 },
        { label: 'Spacer HPHT Rheology', id: 104 },
    ];

    readonly spacerCol1 = ['Spacer Compatibility', 'Spacer QAQC', 'Mixability'];
    readonly spacerCol2 = ['Spacer SSST', 'Spacer HPHT Rheology'];

    constructor(
        public dialogRef: MatDialogRef<DuplicateSpacerDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DuplicateSpacerDialogData,
        private projectDetailsService: ProjectDetailsService
    ) { }

    onRedesignChange(): void {
        if (!this.redesign) {
            this.redesignReason = null;
            this.selectedTests = {};
            this.comments = '';
        }
    }

    onReasonChange(): void {
        if (this.redesignReason !== 'Compliance') {
            this.selectedTests = {};
        }
    }

    get isValid(): boolean {
        if (!this.redesign) return true;
        return !!this.redesignReason;
    }

    onOk(): void {
        if (!this.isValid || this.isSaving) return;

        let reasonTypeId: number | null = null;
        if (this.redesign && this.redesignReason) {
            reasonTypeId = this.getSlurryRedesignReasonType(this.redesignReason);
        }

        const complianceTestIds = this.redesign && this.redesignReason === 'Compliance'
            ? this.getSelectedComplianceTestIds()
            : [];

        const redesignReason = this.redesign && this.redesignReason != null
            ? { reasonTypeId: this.getSlurryRedesignReasonType(this.redesignReason), comments: this.comments, complianceTestIds }
            : null;

        this.isSaving = true;
        this.saveError = null;

        this.projectDetailsService
            .duplicateSpacerMix(this.data.slurryId, this.data.projectId, redesignReason)
            .subscribe({
                next: (response: any) => {
                    this.dialogRef.close(response.id);
                },
                error: (err: any) => {
                    this.isSaving = false;
                    this.saveError = err?.error?.detail ?? err?.message ?? 'Duplicate failed.';
                }
            });
    }

    onCancel(): void {
        this.dialogRef.close(undefined);
    }

    private getSlurryRedesignReasonType(reason: 'Compliance' | 'Logistics' | 'Cost'): number {
        switch (reason) {
            case 'Compliance': return 1;
            case 'Logistics': return 2;
            case 'Cost': return 3;
        }
    }

    private getSelectedComplianceTestIds(): number[] {
        if (this.data.type === 'Slurry') {
            return Object.entries(this.selectedTests)
                .filter(([, checked]) => checked)
                .map(([name]) => this.slurryComplianceTestTypeMap[name])
                .filter(id => id != null);
        }
        return this.spacerComplianceTests
            .filter(t => this.selectedTests[t.label])
            .map(t => t.id);
    }
}
